import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Landmark, TrendingUp, Receipt, Loader2, Wallet } from "lucide-react";

const gbp = (v) => "£" + (Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Section({ title, icon: Icon, rows, total }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Icon className="w-4 h-4 text-indigo-500" /><h2 className="text-sm font-bold text-slate-900">{title}</h2>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="px-5 py-8 text-center text-sm text-slate-400">No data</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-600">{r.label}</span>
              <span className="font-semibold text-slate-800">{gbp(r.value)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between px-5 py-3 text-sm bg-slate-50">
            <span className="font-semibold text-slate-700">Total</span>
            <span className="font-bold text-slate-900">{gbp(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FinancePnL() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/finance/pnl").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="p-8 text-slate-500" data-testid="finance-pnl-error">Unable to load finance data.</div>;
  if (!d) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading finance…</div>;
  const cards = [
    ["Revenue", d.revenue, "text-slate-900"],
    ["Total costs", d.costs_total, "text-slate-900"],
    ["Expenses", d.expenses_total || 0, "text-amber-600"],
    ["Supplier bills", d.bills_total || 0, "text-amber-600"],
    [`Gross profit (${d.margin_pct}%)`, d.gross_profit, d.gross_profit >= 0 ? "text-emerald-600" : "text-rose-600"],
  ];
  const liab = d.liabilities || {};
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-finance-pnl">
      <div className="flex items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Operational summary (legacy)</h1>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="pnl-basis-note">All-time, inc VAT, accepted quotes + web sales · Not the company P&amp;L</p>
        </div>
      </div>
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="pnl-legacy-banner">
        This is a legacy operational summary and its figures differ from the company P&amp;L. Use <Link to="/finance/reports?report=pnl" className="font-semibold underline" data-testid="pnl-legacy-link">Reports → Management P&amp;L</Link> (ex VAT, date-filtered) as the main report.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {cards.map(([l, v, cls]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={`pnl-card-${l}`}>
            <div className="text-xs text-slate-400">{l}</div>
            <div className={`text-2xl font-bold mt-1 ${cls}`}>{gbp(v)}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mb-6 text-sm" data-testid="pnl-liabilities">
        <span className="rounded-lg bg-slate-100 px-3 py-2 text-slate-600">Outstanding liabilities (not in profit):</span>
        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="pnl-liab-bills">Unpaid bills <b>{gbp(liab.unpaid_bills)}</b></span>
        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="pnl-liab-expenses">Expenses awaiting reimbursement <b>{gbp(liab.expenses_awaiting_reimbursement)}</b></span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Revenue by source" icon={TrendingUp} rows={d.revenue_by_source} total={d.revenue} />
        <Section title="Costs by account type" icon={Receipt} rows={d.costs_by_account} total={d.costs_total} />
        <Section title="Approved expenses by category" icon={Wallet} rows={d.expenses_by_category} total={d.expenses_total} />
        <Section title="Supplier bills by category" icon={Landmark} rows={d.bills_by_category} total={d.bills_total} />
      </div>
    </div>
  );
}
